import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadGoogleCharts } from '../Admin/googleChartsLoader';
import ApiURl from '../Paths/Api';

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [cumulativeData, setCumulativeData] = useState({
    totalAttendees: 0,
    totalAttended: 0,
    totalNotAttended: 0,
    byRole: {},
    meetingStatus: { completed: 0, pending: 0 }
  });
  const [showEventModal, setShowEventModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const attendeePieChartRef = useRef(null);
  const roleChartRef = useRef(null);
  const cumulativeAttendeeChartRef = useRef(null);
  const cumulativeMeetingChartRef = useRef(null);

  // Cleanup
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch events
  const fetchEvents = async () => {
    if (!isMounted.current) return;
    setLoading(true);
    setError(null);
    try {
      const phone = localStorage.getItem('phone') || '';
      if (!phone) {
        throw new Error('Phone number not found in localStorage');
      }
      const response = await fetch(`${ApiURl}/get_meetings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      const data = await response.json();
      if (!isMounted.current) return;
      if (data.success) {
        const eventsData = data.data || [];
        setEvents(eventsData);

        // Calculate cumulative data
        calculateCumulativeData(eventsData);

        // Select the first event by default if available
        if (eventsData.length > 0 && !selectedEvent) {
          setSelectedEvent(eventsData[0]);
          prepareEventDetails(eventsData[0]);
        }
      } else {
        setError(data.message || 'Failed to fetch events');
        setEvents([]);
      }
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message || 'An error occurred while fetching events');
      setEvents([]);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Calculate cumulative data from all events
  const calculateCumulativeData = (eventsData) => {
    let totalAttendees = 0;
    let totalAttended = 0;
    let totalNotAttended = 0;
    const byRole = {};
    const meetingStatus = { completed: 0, pending: 0 };

    eventsData.forEach(event => {
      // Count meeting status
      const status = (event.status || '').toLowerCase();
      if (status === 'completed') {
        meetingStatus.completed++;
      } else if (status === 'pending') {
        meetingStatus.pending++;
      }

      // Count attendees
      if (event.attendees) {
        totalAttendees += event.attendees.length;
        event.attendees.forEach(attendee => {
          if (attendee.attended) {
            totalAttended++;
          } else {
            totalNotAttended++;
          }

          // Count by role
          const role = attendee.role || 'Unknown';
          if (!byRole[role]) {
            byRole[role] = { total: 0, attended: 0 };
          }
          byRole[role].total++;
          if (attendee.attended) {
            byRole[role].attended++;
          }
        });
      }
    });

    setCumulativeData({
      totalAttendees,
      totalAttended,
      totalNotAttended,
      byRole,
      meetingStatus
    });
  };

  // Prepare event details
  const prepareEventDetails = (event) => {
    if (!event) return;

    const details = {
      totalAttendees: event.attendees ? event.attendees.length : 0,
      attended: event.attendees ? event.attendees.filter(a => a.attended).length : 0,
      notAttended: event.attendees ? event.attendees.filter(a => !a.attended).length : 0,
      byRole: {}
    };

    // Count attendees by role
    if (event.attendees) {
      event.attendees.forEach(attendee => {
        const role = attendee.role || 'Unknown';
        if (!details.byRole[role]) {
          details.byRole[role] = { total: 0, attended: 0 };
        }
        details.byRole[role].total++;
        if (attendee.attended) {
          details.byRole[role].attended++;
        }
      });
    }

    setEventDetails(details);
  };

  // Draw charts for selected event
  const drawCharts = () => {
    if (!window.google || !window.google.visualization) return;

    try {
      // Draw cumulative charts if we have data
      if (cumulativeData.totalAttendees > 0) {
        // Cumulative Attendee Pie Chart
        const cumulativeAttendeeData = new window.google.visualization.DataTable();
        cumulativeAttendeeData.addColumn('string', 'Status');
        cumulativeAttendeeData.addColumn('number', 'Count');
        cumulativeAttendeeData.addRows([
          ['Attended', cumulativeData.totalAttended],
          ['Not Attended', cumulativeData.totalNotAttended],
        ]);

        const cumulativeAttendeeChart = new window.google.visualization.PieChart(cumulativeAttendeeChartRef.current);
        cumulativeAttendeeChart.draw(cumulativeAttendeeData, {
          title: 'Cumulative Attendee Status',
          is3D: true,
          colors: ['#2dd4bf', '#f43f5e'],
          backgroundColor: 'transparent',
          width: '100%',
          height: 240,
          chartArea: { width: '90%', height: '80%' },
          fontSize: 12,
          legend: { position: 'labeled', textStyle: { fontSize: 12 } }
        });

        // Cumulative Meeting Status Pie Chart
        const cumulativeMeetingData = new window.google.visualization.DataTable();
        cumulativeMeetingData.addColumn('string', 'Status');
        cumulativeMeetingData.addColumn('number', 'Count');
        cumulativeMeetingData.addRows([
          ['Completed', cumulativeData.meetingStatus.completed],
          ['Pending', cumulativeData.meetingStatus.pending],
        ]);

        const cumulativeMeetingChart = new window.google.visualization.PieChart(cumulativeMeetingChartRef.current);
        cumulativeMeetingChart.draw(cumulativeMeetingData, {
          title: 'Cumulative Meeting Status',
          is3D: true,
          colors: ['#2dd4bf', '#f43f5e'],
          backgroundColor: 'transparent',
          width: '100%',
          height: 240,
          chartArea: { width: '90%', height: '80%' },
          fontSize: 12,
          legend: { position: 'labeled', textStyle: { fontSize: 12 } }
        });
      }

      // Draw event-specific charts if we have a selected event
      if (eventDetails) {
        // Attendee Pie Chart for selected event
        const attendeePieData = new window.google.visualization.DataTable();
        attendeePieData.addColumn('string', 'Status');
        attendeePieData.addColumn('number', 'Count');
        attendeePieData.addRows([
          ['Attended', eventDetails.attended],
          ['Not Attended', eventDetails.notAttended],
        ]);

        const attendeePieChart = new window.google.visualization.PieChart(attendeePieChartRef.current);
        attendeePieChart.draw(attendeePieData, {
          title: 'Attendee Status',
          is3D: true,
          colors: ['#2dd4bf', '#f43f5e'],
          backgroundColor: 'transparent',
          width: '100%',
          height: 200,
          chartArea: { width: '90%', height: '80%' },
          fontSize: 12,
          legend: { position: 'labeled', textStyle: { fontSize: 12 } }
        });

        // Role Distribution Chart for selected event
        if (Object.keys(eventDetails.byRole).length > 0) {
          const roleData = new window.google.visualization.DataTable();
          roleData.addColumn('string', 'Role');
          roleData.addColumn('number', 'Total');
          roleData.addColumn('number', 'Attended');

          Object.entries(eventDetails.byRole).forEach(([role, data]) => {
            roleData.addRow([role, data.total, data.attended]);
          });

          const roleChart = new window.google.visualization.ColumnChart(roleChartRef.current);
          roleChart.draw(roleData, {
            title: 'Attendance by Role',
            backgroundColor: 'transparent',
            colors: ['#6366f1', '#2dd4bf'],
            width: '100%',
            height: 200,
            chartArea: { width: '85%', height: '75%' },
            bar: { groupWidth: '60%' },
            fontSize: 12,
            hAxis: { textStyle: { fontSize: 12 } },
            legend: { position: 'top', textStyle: { fontSize: 12 } }
          });
        }
      }
    } catch (err) {
      console.error('Error drawing charts:', err);
    }
  };

  // Load Google Charts
  useEffect(() => {
    if (!hasLoadedOnce) {
      loadGoogleCharts()
        .then(() => {
          window.google.charts.setOnLoadCallback(() => {
            fetchEvents();
            setHasLoadedOnce(true);
          });
        })
        .catch((err) => {
          console.error('Error loading Google Charts:', err);
          setError('Failed to load charts library');
          setLoading(false);
        });
    }
  }, [hasLoadedOnce]);

  // Redraw charts when event details or cumulative data changes
  useEffect(() => {
    if (window.google && window.google.charts) {
      window.google.charts.setOnLoadCallback(drawCharts);
    }
  }, [eventDetails, cumulativeData]);

  // Open event modal
  const openEventModal = (event) => {
    setSelectedEvent(event);
    prepareEventDetails(event);
    setShowEventModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans flex flex-col">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-400 to-cyan-500 text-white p-4 fixed w-full z-30 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-3 md:hidden focus:outline-none hover:bg-teal-600 p-2 rounded-full transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-lg font-bold tracking-tight">Event Manager</h1>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-red-500 rounded-full hover:bg-red-600 transition-all duration-300 text-sm font-medium shadow-sm hover:scale-105"
          >
            Logout
          </button>
        </div>
      </nav>

      {/* Sidebar */}
      <div
        className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
      >
        <button
          className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Dashboard"
          onClick={() => navigate('/dashboard')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Create Meeting"
          onClick={() => navigate('/create-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="View Meetings"
          onClick={() => navigate('/view-meetings')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invites"
          onClick={() => navigate('/send-invites')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Search Meetings"
          onClick={() => navigate('/search-form')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invite Without Pay"
          onClick={() => navigate('/adminsend')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Restrict Meeting"
          onClick={() => navigate('/restrict-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="pt-16 md:pl-20 p-4 max-w-7xl mx-auto flex-1 flex flex-col h-[calc(100vh-3.5rem)]">
        <h1 className="text-lg font-semibold text-gray-900 mb-4">Event Dashboard</h1>

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-600 rounded-md text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-600">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
            <p className="ml-3 text-sm">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="flex-1 flex items-center justify-center bg-white p-6 rounded-lg shadow-sm">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">No Events Found</h2>
              <p className="text-gray-600 text-sm mb-4">It looks like you haven't created any events yet. Get started by creating your first event!</p>
              <button
                onClick={() => navigate('/create-meeting')}
                className="px-4 py-2 bg-gradient-to-r from-teal-400 to-cyan-500 text-white rounded-full hover:from-teal-500 hover:to-cyan-600 transition-all duration-300 text-sm font-medium shadow-sm hover:scale-105"
              >
                Create an Event
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Cumulative Statistics Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
              <h2 className="text-md font-semibold text-gray-800 mb-4">Cumulative Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">All Meetings Attendee Status</h3>
                  <div ref={cumulativeAttendeeChartRef} className="h-64 w-full"></div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                    <div className="p-2 bg-teal-50 rounded-md">
                      <p className="text-xs text-teal-700">Total Attended</p>
                      <p className="text-lg font-semibold text-teal-800">{cumulativeData.totalAttended}</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded-md">
                      <p className="text-xs text-red-700">Total Not Attended</p>
                      <p className="text-lg font-semibold text-red-800">{cumulativeData.totalNotAttended}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">All Meetings Status</h3>
                  <div ref={cumulativeMeetingChartRef} className="h-64 w-full"></div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                    <div className="p-2 bg-teal-50 rounded-md">
                      <p className="text-xs text-teal-700">Completed</p>
                      <p className="text-lg font-semibold text-teal-800">{cumulativeData.meetingStatus.completed}</p>
                    </div>
                    <div className="p-2 bg-yellow-50 rounded-md">
                      <p className="text-xs text-yellow-700">Pending</p>
                      <p className="text-lg font-semibold text-yellow-800">{cumulativeData.meetingStatus.pending}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Events Grid */}
            <div className="bg-white p-4 rounded-lg shadow-sm flex-1 overflow-hidden flex flex-col">
              <h2 className="text-md font-semibold text-gray-800 mb-4">Your Events</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-y-auto pb-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white p-4 rounded-lg shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md border border-gray-100"
                    onClick={() => openEventModal(event)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">{event.purpose || 'No purpose'}</h3>
                        <p className="text-xs text-gray-500 mt-1">{new Date(event.time).toLocaleDateString()}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${event.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {event.status || 'N/A'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-gray-50 p-2 rounded text-center">
                        <p className="text-xs text-gray-600">Attendees</p>
                        <p className="font-semibold text-gray-800">{event.attendees ? event.attendees.length : 0}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded text-center">
                        <p className="text-xs text-gray-600">Present</p>
                        <p className="font-semibold text-teal-600">
                          {event.attendees ? event.attendees.filter(a => a.attended).length : 0}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>{new Date(event.time).toLocaleTimeString()}</span>
                      <span>{event.location || 'No location'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Event Detail Modal */}
        {showEventModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800">{selectedEvent.purpose || 'No purpose'}</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {new Date(selectedEvent.time).toLocaleString()} | {selectedEvent.location || 'No location specified'}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedEvent.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {selectedEvent.status || 'N/A'}
                    </span>
                    <button
                      onClick={() => setShowEventModal(false)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Attendee Status Chart */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Attendee Status</h3>
                    <div ref={attendeePieChartRef} className="h-60 w-full"></div>
                    {eventDetails && (
                      <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                        <div className="p-2 bg-teal-50 rounded-md">
                          <p className="text-xs text-teal-700">Attended</p>
                          <p className="text-lg font-semibold text-teal-800">{eventDetails.attended}</p>
                        </div>
                        <div className="p-2 bg-red-50 rounded-md">
                          <p className="text-xs text-red-700">Not Attended</p>
                          <p className="text-lg font-semibold text-red-800">{eventDetails.notAttended}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Role Distribution Chart */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Attendance by Role</h3>
                    <div ref={roleChartRef} className="h-60 w-full"></div>
                  </div>
                </div>

                {/* Attendees List */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    Attendees ({selectedEvent.attendees?.length || 0})
                  </h3>

                  {selectedEvent.attendees && selectedEvent.attendees.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                      {selectedEvent.attendees.map((attendee, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded-md border ${attendee.attended ? 'border-teal-200 bg-teal-50' : 'border-gray-200 bg-white'}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-800 truncate">{attendee.name || 'No name'}</p>
                              <p className="text-xs text-gray-600 mt-1">{attendee.role || 'No role'}</p>
                            </div>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${attendee.attended ? 'bg-teal-100 text-teal-800' : 'bg-red-100 text-red-800'}`}>
                              {attendee.attended ? 'Attended' : 'Not Attended'}
                            </span>
                          </div>
                          {attendee.email && (
                            <p className="text-xs text-gray-500 mt-2 truncate">{attendee.email}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No attendees found for this event.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;